import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Search,
  User,
  ShoppingBag,
  Menu,
  LogOut,
  Heart,
  Package,
  BookOpen,
  ClipboardList,
  FileDown,
  Layers
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useCartStore } from '@/stores/cartStore';
import { useFavoritesStore } from '@/stores/favoritesStore';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { SmartSearch } from '@/components/search/SmartSearch';
import { DesktopMegaMenu } from '@/components/layout/menu/DesktopMegaMenu';
import { MobileMenuDrawer } from '@/components/layout/menu/MobileMenuDrawer';
import { useOctoberRosa } from '@/hooks/useOctoberRosa';

// Legacy ProductsMegaMenu / SegmentosMegaMenu are kept in the repo but no longer rendered here.

export const Header = () => {
  const octoberRosa = useOctoberRosa();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const { getItemCount, setIsCartOpen } = useCartStore();
  const { getFavoriteCount } = useFavoritesStore();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const itemCount = getItemCount();
  const favoriteCount = getFavoriteCount();

  const handleSignOut = async () => {
    await signOut();
    toast({
      title: "Até logo!",
      description: "Você saiu da sua conta"
    });
    navigate('/');
  };

  return (
    <>
      <header className="sticky top-0 z-50 bg-v2-bg/85 backdrop-blur-md border-b border-border">
        <div className="container mx-auto px-4">
          <div className="relative flex items-center justify-between h-16 min-[980px]:h-20">
            {/* Mobile: hamburger left */}
            <button
              className="min-[980px]:hidden flex h-12 w-12 -ml-3 items-center justify-center touch-manipulation"
              onClick={() => setIsMobileMenuOpen(true)}
              aria-label="Abrir menu"
              aria-expanded={isMobileMenuOpen}
            >
              <Menu className="w-6 h-6" />
            </button>

            {/* Logo (centered on mobile) */}
            <Link
              to="/"
              className="flex items-center select-none absolute left-1/2 -translate-x-1/2 min-[980px]:static min-[980px]:translate-x-0"
              aria-label={octoberRosa ? 'NAT HOF — Outubro Rosa' : 'nat·hof - Página inicial'}
            >
              <span className="relative inline-flex">
              <img
                src="/assets/logo-nathof.png"
                alt="nat·hof"
                className={`h-7 min-[980px]:h-8 w-auto${octoberRosa ? ' invisible' : ''}`}
                loading="eager"
                fetchPriority="high"
                width="120"
                height="32"
              />
              {octoberRosa && (
                <>
                  <span className="october-rosa-logo absolute inset-0" aria-hidden="true" />
                  <svg className="october-rosa-ribbon absolute -right-5 top-1/2 -translate-y-1/2" width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M8 9C5 2 19 2 16 9L7 22L3 18L16 5M8 5L21 18L17 22L8 9" stroke="currentColor" strokeWidth="3" strokeLinejoin="round" />
                  </svg>
                </>
              )}
              </span>
            </Link>

            <DesktopMegaMenu />

            {/* Right Actions */}
            <div className="flex items-center gap-1 md:gap-2 ml-auto">
              <Button
                variant="ghost"
                size="icon"
                className="hidden min-[980px]:flex"
                aria-label="Buscar"
                onClick={() => setIsSearchOpen(true)}
              >
                <Search className="w-5 h-5" />
              </Button>

              <Button
                variant="ghost"
                size="icon"
                className="relative hidden min-[980px]:inline-flex"
                aria-label="Favoritos"
                asChild
              >
                <Link to={user ? "/favoritos" : "/auth"}>
                  <Heart className="w-5 h-5" />
                  {favoriteCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-destructive text-destructive-foreground text-[10px] rounded-full flex items-center justify-center font-medium">
                      {favoriteCount > 9 ? '9+' : favoriteCount}
                    </span>
                  )}
                </Link>
              </Button>

              <div className="hidden min-[980px]:block">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="Minha conta">
                    <User className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-popover z-50">
                  {user ? (
                    <>
                      <div className="px-3 py-2 border-b border-border">
                        <p className="text-sm font-medium text-foreground truncate">
                          {user.email}
                        </p>
                        <p className="text-xs text-muted-foreground">Minha Conta</p>
                      </div>
                      <DropdownMenuItem asChild>
                        <Link to="/perfil" className="cursor-pointer gap-2">
                          <User className="w-4 h-4" />
                          Meu Perfil
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/minha-pele" className="cursor-pointer gap-2">
                          <Layers className="w-4 h-4" />
                          Minha Pele
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/diario" className="cursor-pointer gap-2">
                          <BookOpen className="w-4 h-4" />
                          Diário de Pele
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/perfil#minha-rotina" className="cursor-pointer gap-2">
                          <ClipboardList className="w-4 h-4" />
                          Minha Rotina
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/relatorio" className="cursor-pointer gap-2">
                          <FileDown className="w-4 h-4" />
                          Relatório p/ Profissional
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/pedidos" className="cursor-pointer gap-2">
                          <Package className="w-4 h-4" />
                          Meus Pedidos
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/favoritos" className="cursor-pointer gap-2">
                          <Heart className="w-4 h-4" />
                          Favoritos
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer gap-2 text-destructive focus:text-destructive">
                        <LogOut className="w-4 h-4" />
                        Sair
                      </DropdownMenuItem>
                    </>
                  ) : (
                    <>
                      <DropdownMenuItem asChild>
                        <Link to="/auth" className="cursor-pointer">
                          Entrar
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/auth" className="cursor-pointer">
                          Criar Conta
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              </div>

              {/* Cart */}
              <Button
                variant="ghost"
                size="icon"
                className="relative h-12 w-12 min-[980px]:h-10 min-[980px]:w-10"
                aria-label="Carrinho"
                onClick={() => setIsCartOpen(true)}
              >
                <ShoppingBag className="w-5 h-5" />
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center font-medium">
                    {itemCount}
                  </span>
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <MobileMenuDrawer
        open={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        onOpenSearch={() => setIsSearchOpen(true)}
        favoritesHref={user ? '/favoritos' : '/auth'}
      />

      {/* Smart Search Modal */}
      <SmartSearch isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </>
  );
};
